import React, { createContext, useContext, useCallback, useRef, ReactNode } from 'react';

// Event types for cross-widget communication
export type WidgetEvent = 
  | { type: 'QUIZ_COMPLETED'; data: { quizId: string; interest: string; matches: number } }
  | { type: 'CAREER_SAVED'; data: { careerId: string; title: string } }
  | { type: 'CAREER_UNSAVED'; data: { careerId: string; title: string } }
  | { type: 'CAREER_EXPLORED'; data: { careerId: string; title: string } }
  | { type: 'PATH_STEP_COMPLETED'; data: { pathId: string; stepName: string; stepNumber: number } }
  | { type: 'PATH_COMPLETED'; data: { pathId: string; title: string } }
  | { type: 'COMPARISON_MADE'; data: { careers: string[] } }
  | { type: 'REFRESH_ALL'; data?: undefined };

type EventCallback = (event: WidgetEvent) => void;

interface WidgetEventContextValue {
  subscribe: (callback: EventCallback) => () => void;
  emit: (event: WidgetEvent) => void;
}

const WidgetEventContext = createContext<WidgetEventContextValue>({
  subscribe: () => () => {},
  emit: () => {},
});

export const useWidgetEventBus = () => useContext(WidgetEventContext);

interface WidgetEventProviderProps {
  children: ReactNode;
}

export const WidgetEventProvider = ({ children }: WidgetEventProviderProps) => {
  const listenersRef = useRef<Set<EventCallback>>(new Set());

  const subscribe = useCallback((callback: EventCallback) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  const emit = useCallback((event: WidgetEvent) => {
    console.log('[WidgetEventBus] Emitting event:', event.type);
    listenersRef.current.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[WidgetEventBus] Error in listener:', error);
      }
    });
  }, []);

  return (
    <WidgetEventContext.Provider value={{ subscribe, emit }}>
      {children}
    </WidgetEventContext.Provider>
  );
};

// Hook to subscribe to specific event types
export const useWidgetEvent = (
  eventType: WidgetEvent['type'],
  callback: (event: WidgetEvent) => void
) => {
  const { subscribe } = useWidgetEventBus();
  
  React.useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (event.type === eventType) {
        callback(event);
      }
    });
    return unsubscribe;
  }, [subscribe, eventType, callback]);
};
